// Block D (2026-10-08): the pipeline board (columns as cards, deal cards) and the list view.
// Drag and drop on desktop, the touch drag on a phone, and on every size a ⋯ menu on each card
// with "Move to stage…" (the kit's MoveToSheet, calling the same move as the drag) and
// "Remove from board".
import React, { useState } from 'react';
import { Avatar, Menu, Button, Input, List, ListRow, RowPrimary, v } from '../../ui';
import HealthScoreBadge from './HealthScoreBadge';
import { dealValue, nextStep, lastContactLabel, money, stageTotal, UNASSIGNED_ID } from './crmPipeline';

export function LastContactPill({ contact, flagged }) {
  return (
    <span className={`crm-pill${flagged ? ' crm-pill--flag' : ''}`} title={flagged ? 'Needs a follow-up' : undefined}>
      {lastContactLabel(contact.lastContactedAt, flagged)}
    </span>
  );
}

function ContactCard({ contact, color, currency, isSelected, followUp, dragging, onOpen, onDragStart, onDragEnd, onTouchDragStart, onTouchDrop, onMoveTo, onRemove }) {
  const value = dealValue(contact);
  const step = nextStep(contact);
  const isClient = (contact.tags || []).includes('Client');

  function onTouchStart(e) {
    if (e.target.closest('button, a, input')) return;
    let isDragging = false;
    let overStageId = null;
    const startX = e.touches[0].clientX;
    const startY = e.touches[0].clientY;
    const cardEl = e.currentTarget;
    const timer = { started: Date.now() };

    const onMove = (ev) => {
      const touch = ev.touches[0];
      const dx = touch.clientX - startX; const dy = touch.clientY - startY;
      if (!isDragging) {
        if (Math.hypot(dx, dy) < 8) return;
        // A quick swipe scrolls (the page, or the columns sideways on a phone); a short hold
        // and then a move drags the card.
        if (Date.now() - timer.started < 250) { cleanup(); return; }
        isDragging = true;
        onTouchDragStart(contact.id);
      }
      ev.preventDefault();
      cardEl.classList.add('crm-card--dragging');
      cardEl.style.pointerEvents = 'none';
      const below = document.elementFromPoint(touch.clientX, touch.clientY);
      cardEl.style.pointerEvents = '';
      let node = below;
      while (node && !node.dataset?.stageId) node = node.parentElement;
      overStageId = node?.dataset?.stageId ?? null;
    };
    const cleanup = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      cardEl.classList.remove('crm-card--dragging');
    };
    const onEnd = () => {
      cleanup();
      if (isDragging) onTouchDrop(overStageId);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }

  return (
    <div
      className={`crm-card${isSelected ? ' crm-card--selected' : ''}${dragging ? ' crm-card--dragging' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, contact.id)}
      onDragEnd={onDragEnd}
      onTouchStart={onTouchStart}
      onClick={(e) => { if (!e.target.closest('button, a, [role="menu"]')) onOpen(contact.id); }}
      data-tour-id="crm-contact-card"
    >
      <div className="crm-card__top">
        <Avatar name={contact.name} size={36} color={color} />
        <div className="crm-card__who">
          <button type="button" className="crm-card__name" onClick={() => onOpen(contact.id)}>{contact.name}</button>
          {contact.company && <span className="crm-card__company">{contact.company}</span>}
        </div>
        <span className="crm-card__menu">
          <Menu label={`Actions for ${contact.name}`} title={contact.name} items={[
            { label: 'Move to stage…', onSelect: () => onMoveTo(contact) },
            { label: 'Remove from board', danger: true, onSelect: () => onRemove(contact) },
          ]} />
        </span>
      </div>
      <div className="crm-card__pills">
        {value !== null && <span className="crm-pill crm-pill--value">{money(value, currency)}</span>}
        <LastContactPill contact={contact} flagged={!!followUp} />
        {contact.isNew && <span className="crm-pill crm-pill--new">NEW</span>}
        {isClient && <HealthScoreBadge score={contact.healthScore} scoredAt={contact.healthScoreAt} data-tour-id="crm-health-badge" />}
      </div>
      {step && <span className="crm-card__next"><b>Next:</b><span>{step}</span></span>}
    </div>
  );
}

function QuickAdd({ stageName, onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    try { const ok = await onAdd(n); if (ok) setName(''); } finally { setBusy(false); }
  }
  if (!open) return <button type="button" className="crm-col__add" onClick={() => setOpen(true)}>+ Add to {stageName}</button>;
  return (
    <div className="crm-quick">
      <Input autoFocus aria-label={`New contact in ${stageName}`} placeholder="Name, then Enter" value={name} disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }} />
      <div className="crm-quick__buttons">
        <Button variant="confirm" size="sm" onClick={save} disabled={!name.trim() || busy}>{busy ? 'Adding…' : 'Add'}</Button>
        <Button variant="tertiary" size="sm" onClick={() => { setOpen(false); setName(''); }}>Cancel</Button>
      </div>
    </div>
  );
}

export function KanbanColumn({ stage, color, cardColor, currency, selectedId, followUps, draggingId, onOpen, onDragStart, onDragEnd, onDrop, onTouchDragStart, onTouchDrop, onMoveTo, onRemove, onQuickAdd }) {
  const [over, setOver] = useState(false);
  const contacts = stage.contacts || [];
  const total = stageTotal(stage);
  const droppable = stage.id !== UNASSIGNED_ID;
  return (
    <section
      className={`crm-col${over ? ' crm-col--over' : ''}`}
      aria-label={`${stage.name}, ${contacts.length} ${contacts.length === 1 ? 'contact' : 'contacts'}`}
      data-stage-id={droppable ? stage.id : undefined}
      onDragOver={droppable ? (e) => { e.preventDefault(); if (!over) setOver(true); } : undefined}
      onDragLeave={droppable ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); } : undefined}
      onDrop={droppable ? (e) => { e.preventDefault(); setOver(false); onDrop(stage.id); } : undefined}
    >
      <div className="crm-col__head" style={{ '--ui-bg': v(color) }}>
        <div className="crm-col__row">
          <span className="crm-col__name">{stage.name}</span>
          <span className="crm-col__count">{contacts.length}</span>
        </div>
        <span className="crm-col__total">{total === null ? 'No values yet' : money(total, currency)}</span>
      </div>
      <div className="crm-col__cards">
        {contacts.map((c) => (
          <ContactCard key={c.id} contact={c} color={cardColor} currency={currency} isSelected={c.id === selectedId}
            followUp={followUps[c.id]} dragging={draggingId === c.id}
            onOpen={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd} onTouchDragStart={onTouchDragStart} onTouchDrop={onTouchDrop}
            onMoveTo={onMoveTo} onRemove={onRemove} />
        ))}
        {contacts.length === 0 && (
          <div className="crm-col__empty">{stage.unassigned ? 'Nothing unassigned' : over ? 'Drop here' : 'Drag a contact here'}</div>
        )}
      </div>
      {!stage.unassigned && onQuickAdd && <QuickAdd stageName={stage.name} onAdd={(name) => onQuickAdd(name, stage.id)} />}
    </section>
  );
}

const LIST_COLUMNS = [
  { key: 'contact', label: 'Contact', width: 'minmax(160px, 2fr)', role: 'primary' },
  { key: 'stage', label: 'Stage', width: 'minmax(100px, 1.1fr)', role: 'status' },
  { key: 'value', label: 'Value', width: '96px', align: 'end' },
  { key: 'last', label: 'Last contact', width: 'minmax(110px, 1fr)' },
  { key: 'next', label: 'Next step', width: 'minmax(100px, 1.6fr)' },
];

export function PipelineList({ lanes, colorOf, currency, followUps, onOpen }) {
  const rows = lanes.flatMap((s, i) => (s.contacts || []).map((c) => ({ c, s, color: colorOf(s, i) })));
  return (
    <List label="Pipeline contacts" columns={LIST_COLUMNS}>
      {rows.map(({ c, s, color }) => {
        const value = dealValue(c);
        const step = nextStep(c);
        return (
          <ListRow key={c.id} onOpen={() => onOpen(c.id)} openLabel={`Open ${c.name}`}
            cells={{
              contact: <RowPrimary leading={<Avatar name={c.name} size={36} color={color} />} name={c.name} sub={c.company || undefined} />,
              stage: <span className="crm-pill" style={{ '--ui-bg': v(color) }}>{s.name}</span>,
              value: <span className="crm-list-value">{value === null ? '' : money(value, currency)}</span>,
              last: <LastContactPill contact={c} flagged={!!followUps[c.id]} />,
              next: <span className="crm-list-next">{step}</span>,
            }}
            meta={[value === null ? null : money(value, currency), lastContactLabel(c.lastContactedAt, !!followUps[c.id]), step ? `Next: ${step}` : null].filter(Boolean).join(' · ')}
          />
        );
      })}
    </List>
  );
}
